import * as DocumentPicker from 'expo-document-picker'
import { DragDropContentView } from 'expo-drag-drop-content-view'
import * as FileSystem from 'expo-file-system/legacy'
import { router, useLocalSearchParams } from 'expo-router'
import pQueue from 'p-queue'
import { useCallback, useEffect, useRef, useState } from 'react'
import { LayoutChangeEvent, Platform, View } from 'react-native'
import { StyleSheet } from 'react-native-unistyles'
import { uuidv7 } from 'uuidv7'

import Document from '@/components/fosters/cards/Document'
import Alert from '@/components/shared/Alert'
import Card from '@/components/shared/Card'
import ScanRequestModal from '@/components/shared/ScanRequestModal'
import Snackbar from '@/components/snackbar/Snackbar'
import TourSpot from '@/components/tour/TourSpot'
import UniActivityIndicator from '@/components/unistyles/UniActivityIndicator'
import UniButton from '@/components/unistyles/UniButton'
import UniText from '@/components/unistyles/UniText'
import getUploadedFileId from '@/hooks/uploads/uploadResult'
import useAppTour from '@/hooks/useAppTour'
import useFile from '@/hooks/useFile'
import FosterService from '@/services/fosterService'
import { useAppStore } from '@/store/useAppStore'
import sharedStyles from '@/styles/styles'
import log from '@/utils/datadog/log/log'
import buildOrgPath from '@/utils/orgRoute'
import trpc from '@/utils/trpc'
import { AppTourKeys } from '../../../../constants'

const uploadQueue = new pQueue({ concurrency: 6 })

interface Props {
  disabled: boolean
  fosterId: string
}

const FosterDocumentsCard = ({ disabled, fosterId }: Props) => {
  const { scanIntent, scanUris } = useLocalSearchParams<{
    scanIntent?: string
    scanUris?: string | string[]
  }>()

  const currentOrganization = useAppStore.use.currentOrganization().organization

  const { foster, refreshFoster } = FosterService.useFoster(fosterId)
  const [pendingDocuments, setPendingDocuments] = useState<
    | {
        [index: number]: {
          totalBytes: number
          bytesUploaded: number
        }
      }
    | undefined
  >()

  const { scanFiles, createUploadTasks } = useFile()
  const isUploadingRef = useRef(false)
  const [isUploading, setIsUploading] = useState(false)

  const documentCreateMutation = trpc.foster.documents.create.useMutation()
  const documentDeleteMutation = trpc.foster.documents.delete.useMutation()
  const requestScanMutation = trpc.foster.documents.requestScan.useMutation()

  const [scanRequestModalOpen, setScanRequestModalOpen] = useState(false)

  const hasDocuments = !!foster?.documents?.length

  const [containerWidth, setContainerWidth] = useState(0)

  const onContainerLayout = useCallback((e: LayoutChangeEvent) => {
    setContainerWidth(e.nativeEvent.layout.width)
  }, [])

  const GAP = 16
  const MIN_CARD_WIDTH = 280
  const MAX_COLUMNS = 4

  const columns =
    containerWidth > 0
      ? Math.max(
          1,
          Math.min(
            MAX_COLUMNS,
            Math.floor((containerWidth + GAP) / (MIN_CARD_WIDTH + GAP))
          )
        )
      : 1
  const cardWidth =
    containerWidth > 0
      ? (containerWidth - GAP * (columns - 1)) / columns
      : undefined

  useAppTour({
    enabled: true,
    tourKey: AppTourKeys.FosterDocumentsTab,
  })

  useEffect(() => {
    if (!foster?.documents.filter((doc) => !doc.analysisVersion).length) {
      return
    }

    const timer = setInterval(() => {
      refreshFoster()
    }, 2000)

    return () => clearInterval(timer)
  }, [foster?.documents, refreshFoster])

  const progressCallback = useCallback(
    (progress: FileSystem.UploadProgressData, index: number) => {
      setPendingDocuments((prev) => ({
        ...prev,
        [index]: {
          totalBytes: progress.totalBytesExpectedToSend,
          bytesUploaded: progress.totalBytesSent,
        },
      }))
    },
    [setPendingDocuments]
  )

  const handleDocuments = useCallback(
    async (
      files: {
        fileName: string
        fileUri: string
      }[]
    ) => {
      // Prevent re-entrancy/duplicate triggers
      if (isUploadingRef.current || !files.length) {
        return
      }

      isUploadingRef.current = true
      setIsUploading(true)
      let stage: 'uploading' | 'saving' | 'refreshing' = 'uploading'
      let savedCount = 0
      let failedUploadMessage = ''

      try {
        // Dedupe files by URI in case the caller passed duplicates
        const dedupedFiles = Array.from(
          new Map(files.map((f) => [f.fileUri, f])).values()
        )

        const uploadTasks = createUploadTasks({
          files: dedupedFiles,
          progressCallback: progressCallback,
        })

        const newPendingDocuments: {
          [index: number]: {
            totalBytes: number
            bytesUploaded: number
          }
        } = {}

        uploadTasks.forEach((task, index) => {
          newPendingDocuments[index] = {
            totalBytes: 100,
            bytesUploaded: 0,
          }
        })

        setPendingDocuments(newPendingDocuments)

        // Finish every queued upload before clearing state so late progress
        // from another file cannot leave the card stuck after a failure.
        const uploadResults = await Promise.allSettled(
          uploadTasks.map((task) =>
            uploadQueue.add(async () => {
              const response = await task.uploadAsync()
              return getUploadedFileId(response)
            })
          )
        )
        const fileIds: string[] = []
        const failures: { fileName: string; error: unknown }[] = []
        uploadResults.forEach((result, index) => {
          if (result.status === 'fulfilled' && result.value !== undefined) {
            fileIds.push(result.value)
          } else {
            failures.push({
              fileName: dedupedFiles[index].fileName,
              error:
                result.status === 'rejected'
                  ? result.reason
                  : new Error('Upload did not complete'),
            })
          }
        })

        if (failures.length) {
          log.error('Some document uploads failed.', { failures })
          failedUploadMessage = `${failures.length} upload${failures.length === 1 ? '' : 's'} failed: ${failures.map((failure) => failure.fileName).join(', ')}.`
        }

        if (fileIds.length) {
          stage = 'saving'
          await documentCreateMutation.mutateAsync(
            fileIds.map((fileId) => ({
              fosterId,
              fileId,
              type: 'Uncategorized',
            }))
          )

          savedCount = fileIds.length
          stage = 'refreshing'
          const refreshedFoster = await refreshFoster()
          if (refreshedFoster.isError) {
            throw refreshedFoster.error
          }
        }

        if (failures.length) {
          Snackbar.error(
            `${savedCount} document${savedCount === 1 ? '' : 's'} saved. ${failedUploadMessage} Please retry only the failed files.`
          )
        }
      } catch (error) {
        log.error('There was a problem uploading documents.', { error, stage })
        if (stage === 'saving') {
          Snackbar.error(
            [
              "We couldn't confirm the uploaded files were added to this foster.",
              failedUploadMessage,
              'Refresh the document list before retrying to avoid duplicates.',
            ]
              .filter(Boolean)
              .join(' ')
          )
        } else if (stage === 'refreshing') {
          Snackbar.error(
            [
              `${savedCount} document${savedCount === 1 ? '' : 's'} saved, but the list couldn't refresh.`,
              failedUploadMessage,
              'Refresh the document list before uploading again.',
            ]
              .filter(Boolean)
              .join(' ')
          )
        } else {
          Snackbar.error(
            'There was a problem uploading your documents. Please try again.'
          )
        }
      } finally {
        setPendingDocuments(undefined)
        isUploadingRef.current = false
        setIsUploading(false)
      }
    },
    [
      createUploadTasks,
      documentCreateMutation,
      fosterId,
      progressCallback,
      refreshFoster,
      setPendingDocuments,
    ]
  )

  const scanDocuments = useCallback(async () => {
    const uris = await scanFiles()
    handleDocuments(
      uris?.map((uri) => ({
        fileName: `${uuidv7()}.jpg`,
        fileUri: uri,
      })) ?? []
    )
  }, [handleDocuments, scanFiles])

  const requestScan = useCallback(async () => {
    if (!currentOrganization) return
    try {
      const result = await requestScanMutation.mutateAsync({
        fosterId,
        organizationId: currentOrganization.id,
      })
      setScanRequestModalOpen(false)
      if (result.reason === 'no_devices') {
        Snackbar.info('Install the app on your phone to use scan from web')
      }
    } catch {
      setScanRequestModalOpen(false)
      Snackbar.error('There was a problem sending the scan request')
    }
  }, [currentOrganization, fosterId, requestScanMutation])

  const selectDocuments = useCallback(async () => {
    const result = await DocumentPicker.getDocumentAsync({
      type: ['application/pdf', 'image/*'],
      multiple: true,
    })

    if (!result.assets?.length) {
      return
    }

    if (
      result.assets?.filter((asset) => asset.mimeType?.startsWith('image'))
        .length
    ) {
      Alert.alert(
        'Images Detected',
        'We recommend either uploading a PDF, or using the Scan Documents feature.',
        [
          {
            text: 'Upload Anyway',
            onPress: async () => {
              handleDocuments(
                result.assets.map((asset) => ({
                  fileName: asset.name,
                  fileUri: asset.uri,
                }))
              )
            },
          },
          {
            text: 'Scan Documents',
            style: 'cancel',
            onPress: scanDocuments,
          },
        ]
      )
    } else {
      handleDocuments(
        result.assets.map((asset) => ({
          fileName: asset.name,
          fileUri: asset.uri,
        }))
      )
    }
  }, [handleDocuments, scanDocuments])

  const scanUrisHandledRef = useRef(false)
  useEffect(() => {
    async function handleScanUris() {
      if (scanUris && !pendingDocuments && !scanUrisHandledRef.current) {
        scanUrisHandledRef.current = true
        const uris = Array.isArray(scanUris) ? scanUris : scanUris.split(',')
        router.replace(buildOrgPath(`/fosters/${fosterId}/documents`))

        await handleDocuments(
          uris.map((uri) => ({
            fileName: `${uuidv7()}.jpg`,
            fileUri: uri,
          }))
        )
      }
    }

    handleScanUris()
  }, [fosterId, handleDocuments, pendingDocuments, scanUris])

  const scanIntentHandledRef = useRef(false)
  useEffect(() => {
    if (
      Platform.OS === 'web' ||
      scanIntent !== 'true' ||
      isUploading ||
      pendingDocuments ||
      scanIntentHandledRef.current
    ) {
      return
    }
    scanIntentHandledRef.current = true
    router.replace(buildOrgPath(`/fosters/${fosterId}/documents`))
    scanDocuments()
  }, [fosterId, isUploading, pendingDocuments, scanDocuments, scanIntent])

  if (!currentOrganization) {
    return null
  }

  // group the documents by type in a nested array
  let documentsByType = Object.values(
    foster?.documents.reduce<Record<string, typeof foster.documents>>(
      (acc, document) => {
        document.type = document.analysisVersion
          ? document.type
          : 'NewDocuments'
        const type = document.type
        if (!acc[type]) {
          acc[type] = []
        }
        acc[type].push(document)
        return acc
      },
      {}
    ) ?? {}
  )

  // Filter documents alphabetically by type, but put unidentified documents first
  documentsByType = documentsByType.sort((a, b) => {
    // Check if either group has unidentified documents
    const aHasUnidentified = a.some((doc) => !doc.analysisVersion)
    const bHasUnidentified = b.some((doc) => !doc.analysisVersion)

    // Put unidentified documents first
    if (aHasUnidentified && !bHasUnidentified) return -1
    if (!aHasUnidentified && bHasUnidentified) return 1

    // If both are identified or both unidentified, sort by type
    return a[0].type.localeCompare(b[0].type)
  })

  return (
    <DragDropContentView
      onDrop={async (f) => {
        if (disabled || isUploading || pendingDocuments) return
        const files = f.assets
          .map((file) => {
            const fileUri = file.uri || file.base64
            if (!file.fileName || !fileUri) {
              return undefined
            }

            return {
              fileName: file.fileName,
              fileUri,
            }
          })
          .filter(Boolean) as { fileName: string; fileUri: string }[]

        handleDocuments(files)
      }}
    >
      <Card style={styles.root} title="Documents" titleStyle={styles.title}>
        {!hasDocuments && <UniText>No documents uploaded.</UniText>}
        <View style={sharedStyles.buttonRow}>
          {Platform.OS === 'web' ? (
            <>
              <UniButton
                disabled={
                  disabled ||
                  isUploading ||
                  !!pendingDocuments ||
                  requestScanMutation.isPending
                }
                icon={'scanner'}
                mode="contained"
                onPress={() => setScanRequestModalOpen(true)}
              >
                Scan
              </UniButton>
              <ScanRequestModal
                fosterName={foster?.name ?? 'this foster'}
                isPending={requestScanMutation.isPending}
                onConfirm={requestScan}
                onDismiss={() => setScanRequestModalOpen(false)}
                visible={scanRequestModalOpen}
              />
            </>
          ) : (
            <TourSpot id="foster-documents-tab-ai-explanation">
              <TourSpot id="foster-documents-tab-scan-button">
                <UniButton
                  disabled={disabled || isUploading || !!pendingDocuments}
                  icon={'scanner'}
                  mode="contained"
                  onPress={scanDocuments}
                >
                  Scan Documents
                </UniButton>
              </TourSpot>
            </TourSpot>
          )}
          <UniButton
            disabled={disabled || isUploading || !!pendingDocuments}
            icon={'upload'}
            mode="outlined"
            onPress={selectDocuments}
            style={styles.uploadButton}
          >
            Upload Documents
          </UniButton>
        </View>
        {pendingDocuments && (
          <View style={styles.pendingDocumentsCard}>
            <UniText>
              Uploading {Object.keys(pendingDocuments).length} document
              {Object.keys(pendingDocuments).length > 1 ? 's' : ''}...
            </UniText>
            <UniActivityIndicator />
          </View>
        )}
        <View>
          {hasDocuments &&
            documentsByType.map((documents, index) => {
              return (
                <View
                  key={documents[0].type}
                  style={index > 0 ? styles.documentTypeContainer : undefined}
                >
                  <UniText style={styles.documentType} variant="titleMedium">
                    {documents[0].type.split(/(?=[A-Z])/).join(' ')}
                  </UniText>
                  <View
                    onLayout={onContainerLayout}
                    style={styles.documentCardContainer}
                  >
                    {hasDocuments &&
                      documents.map((document) => {
                        return (
                          <Document
                            cardWidth={cardWidth}
                            createdAt={document.file.createdAt}
                            detailShort={document.detailShort ?? undefined}
                            disabled={disabled}
                            documentId={document.id}
                            documentType={document.type}
                            fileId={document.file.id}
                            fileName={`${foster.name}'s ${document.type} ${document.file.createdAt.toLocaleDateString(
                              'en-US',
                              {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              }
                            )}.${document.file.name.split('.').pop()}`}
                            fosterName={foster.name}
                            key={document.id}
                            mimeType={document.file.mimeType}
                            onDelete={() => {
                              documentDeleteMutation.mutate(
                                {
                                  id: document.id,
                                  organizationId: currentOrganization.id,
                                },
                                {
                                  onSuccess: () => {
                                    refreshFoster()
                                  },
                                }
                              )
                            }}
                            onUpdate={() => {
                              refreshFoster()
                            }}
                            testID={`document-list-item-${index}`}
                            uploadedBy={document.file.uploadedBy}
                          />
                        )
                      })}
                  </View>
                </View>
              )
            })}
        </View>
      </Card>
    </DragDropContentView>
  )
}

const styles = StyleSheet.create((theme) => {
  return {
    documentCardContainer: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 16,
    },
    documentType: {
      color: theme.colors.onSurfaceVariant,
      marginBottom: theme.tokens.spacing[3],
    },
    documentTypeContainer: {
      marginTop: theme.tokens.spacing[4],
    },
    pendingDocumentsCard: {
      alignItems: 'center',
      backgroundColor: theme.colors.surface,
      borderColor: theme.colors.cardBorder,
      borderRadius: theme.tokens.containerBorderRadius,
      borderWidth: 1,
      flex: 1,
      flexDirection: 'row',
      gap: theme.tokens.spacing[4],
      justifyContent: 'space-between',
      padding: theme.tokens.spacing[4],
    },
    root: {
      backgroundColor: theme.colors.background,
      borderWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: theme.tokens.spacing[4],
      padding: 0,
      // On web all of our foster form components live on a single page, and our lack of wrapping card component on Documents
      // means that the documents section is not aligned with the rest of the page. So for web we adding some extra padding so
      // the documents section is aligned with the rest of the page.
      ...Platform.select({
        web: {
          paddingHorizontal: theme.tokens.spacing[4],
        },
        default: {},
      }),
    },
    title: {
      marginBottom: 0,
    },
    uploadButton: {
      backgroundColor: theme.colors.surface,
    },
  }
})

export default FosterDocumentsCard
